// Web hailing's own copy of the web receipt's status wording
// (web-receipt/handler/receiptStatus.helper.ts and receiptPOI.helper.ts), so the ride
// tracker reads like the receipt without changing when the receipt does. Text lives in
// the hailing namespace under steps.ride.tracker.
import { TFunction } from "i18next";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { OrderStatus, RECEIPT_STATUS } from "../../../models/receipt/types";

dayjs.extend(duration);

const KEY = "steps.ride.tracker";

export const toTripPhase = (status: string): OrderStatus => {
  switch (status) {
    case RECEIPT_STATUS.PENDING:
    case RECEIPT_STATUS.PENDING_PAYMENT:
    case RECEIPT_STATUS.ACCEPTED:
      return "scheduled";
    case RECEIPT_STATUS.APPROACHING:
      return "enroute";
    case RECEIPT_STATUS.ARRIVED:
    case RECEIPT_STATUS.ON_GOING:
      return "intransit";
    case RECEIPT_STATUS.SUCCESSFUL:
    case RECEIPT_STATUS.COMPLETED:
      return "completed";
    case RECEIPT_STATUS.CANCELLED:
    case RECEIPT_STATUS.TIMED_OUT:
    case RECEIPT_STATUS.EXPIRED:
    case RECEIPT_STATUS.FAILED:
      return "cancelled";
    default:
      return "scheduled";
  }
};

/** "Today, 2:17 PM", "Tue, 2:17 PM", or the full date for anything further away. */
const dayAndTime = (t: TFunction, time: dayjs.Dayjs): string => {
  const now = dayjs();
  if (time.isSame(now, "day")) return `${t(`${KEY}.commonToday`)}, ${time.format("h:mm A")}`;
  if (time.isSame(now.subtract(1, "day"), "day")) {
    return `${t(`${KEY}.commonYesterday`)}, ${time.format("h:mm A")}`;
  }
  if (time.isSame(now.add(1, "day"), "day")) {
    return `${t(`${KEY}.commonTomorrow`)}, ${time.format("h:mm A")}`;
  }
  const weekday = t(`${KEY}.common${time.format("dddd")}`);
  if (time.isAfter(now, "day")) return `${weekday}, ${time.format("h:mm A")}`;
  return t(`${KEY}.commonDateTime`, {
    weekday,
    year: time.format("YYYY"),
    month: time.format("MM"),
    day: time.format("DD"),
    h: time.format("h"),
    mm: time.format("mm"),
    meridiem: time.format("A"),
  });
};

/** The headline: pickup time, minutes away, pickup or destination, or the outcome. */
export const getTripTitle = (
  t: TFunction,
  phase: OrderStatus,
  args: {
    time?: string | undefined;
    etaSeconds?: number | undefined;
    rawStatus: string;
    pickupName: string;
    dropoffName: string;
  },
): string => {
  const tripTime = args.time ? dayjs(args.time) : null;
  switch (phase) {
    case "scheduled":
      return tripTime ? dayAndTime(t, tripTime) : "";
    case "enroute": {
      const minutes =
        typeof args.etaSeconds === "number" && args.etaSeconds > 0
          ? Math.round(args.etaSeconds / 60)
          : tripTime
            ? tripTime.diff(dayjs(), "minutes")
            : 0;
      return t(`${KEY}.driverEnrouteSubtitle`, { n: Math.max(0, minutes) });
    }
    case "intransit":
      return args.rawStatus === RECEIPT_STATUS.ARRIVED ? args.pickupName : args.dropoffName;
    case "completed":
      return t(`${KEY}.tripCompletedTitle`);
    case "cancelled":
      return t(`${KEY}.tripCancelledTitle`);
    default:
      return "";
  }
};

/** The line with the headline: what is happening, or when (for a finished trip). */
export const getTripSubtitle = (
  t: TFunction,
  phase: OrderStatus,
  args: { updatedAt?: string | undefined; rawStatus: string },
): string => {
  switch (phase) {
    case "scheduled":
      return t(`${KEY}.tripScheduledTitle`);
    case "enroute":
      return t(`${KEY}.driverEnrouteTitle`);
    case "intransit":
      return args.rawStatus === RECEIPT_STATUS.ARRIVED
        ? t(`${KEY}.driverArrived_title`)
        : t(`${KEY}.headingToDestinationTitle`);
    case "completed":
    case "cancelled":
      return args.updatedAt ? dayAndTime(t, dayjs(args.updatedAt)) : "";
    default:
      return "";
  }
};

/** The right-hand note: time until pickup, or the arrival time while on the way. */
export const getTripTimeInfo = (
  t: TFunction,
  phase: OrderStatus,
  args: { time?: string | undefined; etaSeconds?: number | undefined },
): string => {
  if (!args.time) return "";
  if (phase === "scheduled") {
    const diffMs = dayjs(args.time).diff(dayjs());
    if (diffMs <= 0) return "";
    const dur = dayjs.duration(diffMs);
    return t(`${KEY}.tripScheduledCountdown`, {
      hr: Math.floor(dur.asHours()),
      min: dur.minutes(),
    });
  }
  if (phase === "intransit" && typeof args.etaSeconds === "number" && args.etaSeconds > 0) {
    const eta = dayjs().add(args.etaSeconds, "second");
    return t(`${KEY}.headingToDestinationEta`, {
      "h:mm": eta.format("h:mm"),
      meridiem: eta.format("A"),
    });
  }
  return "";
};

/** The pickup time on the places card: "Today, 2:17 PM" or "2026-10-26, 2:17 PM". */
export const getPickupTime = (t: TFunction, isoString?: string | null): string => {
  if (!isoString) return "";
  const time = dayjs(isoString);
  return time.isSame(dayjs(), "day")
    ? `${t(`${KEY}.commonToday`)}, ${time.format("h:mm A")}`
    : time.format("YYYY-MM-DD, h:mm A");
};
